<script setup lang="ts">
import { computed, defineProps, onMounted, onUnmounted, ref, watch } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import interactionPlugin from '@fullcalendar/interaction'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import Datepicker from '@vuepic/vue-datepicker'
import EventEdit from '@/components/features/events/EventEdit.vue'
import EventCreate from '@/components/features/events/EventCreate.vue'
import CommercialFilter from '@/components/filters/CommercialFilter.vue'
import { storeToRefs } from 'pinia'
import { useEventStore } from '@/stores/useEventStore'
import { useUserStore } from '@/stores/useUserStore'
import { colors } from '@/utils/colors'
import { calculateDurationWithFormat, removeTimeZone } from '@/utils/dateUtils'
import { useNavBarStore } from '@/stores/navBarStore'
import type { Partner } from '@/api-swagger'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()
const { drawerOpen } = storeToRefs(useNavBarStore())
const eventStore = useEventStore()
const userStore = useUserStore()
const { userConnected, isManager } = storeToRefs(userStore)
const { filters, resourcesFullcalendar, eventsFullcalendar, eventsUser, eventsPartner, loadingDiary } = storeToRefs(eventStore)
const calendarRef = ref<InstanceType<typeof FullCalendar> | null>(null)
const selectedDate = ref<Date>(new Date())
const selectedEventId = ref<number | null>(null)
const showEventModal = ref(false)
const isDataLoaded = ref(false)
// Global mouse-down state used to suppress tooltips while the mouse button is held
const isMouseDownGlobal = ref(false)
const onGlobalMouseDown = () => {
  isMouseDownGlobal.value = true
}
const onGlobalMouseUp = () => {
  isMouseDownGlobal.value = false
}

const props = defineProps({
  showAddButton: {
    type: Boolean,
    default: true,
  },
  showFilterCommercial: {
    type: Boolean,
    default: true,
  },
  partnerOnly: {
    type: Boolean,
    default: false,
  },
})

// En mode partnerOnly, combiner mes events + events partner pour le calendrier
function toCalendarEvent(event: any, color?: string) {
  const start = event.start?.replace(' ', 'T')
  const stop = (event.stop ?? event.end)?.replace(' ', 'T')
  return {
    id: String(event.id),
    title: event.name ?? event.title ?? '',
    start,
    end: stop,
    resourceIds: ['a'],
    ...(color ? { color } : {}),
  }
}

const calendarEvents = computed(() => {
  if (!props.partnerOnly) return eventsFullcalendar.value

  const myEvents = eventsUser.value.map((e: any) => toCalendarEvent(e))
  const myIds = new Set(myEvents.map((e) => e.id))

  const partnerEvents = eventsPartner.value
    .filter((e: any) => !myIds.has(String(e.id)))
    .map((e: any) => toCalendarEvent(e, '#6366f1'))

  return [...myEvents, ...partnerEvents]
})

function getVisibleDateRange(): { startDate: string; endDate: string } {
  const api = calendarRef.value?.getApi()
  if (api) {
    const view = api.view
    return {
      startDate: view.activeStart.toISOString().split('T')[0],
      endDate: view.activeEnd.toISOString().split('T')[0],
    }
  }
  // Fallback : mois courant avec marge
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 0)
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  }
}

onMounted(async () => {
  if (!props.partnerOnly) {
    const range = getVisibleDateRange()
    await eventStore.getEventsSubordinatesId(range.startDate, range.endDate)
  } else {
    await eventStore.getMyEvents()
  }
  setTimeout(() => {
    isDataLoaded.value = true
  }, 200)
  document.addEventListener('mousedown', onGlobalMouseDown)
  document.addEventListener('mouseup', onGlobalMouseUp)
})


onUnmounted(() => {
  document.removeEventListener('mousedown', onGlobalMouseDown)
  document.removeEventListener('mouseup', onGlobalMouseUp)
})

const shouldShowCalendar = computed(() => {
  return calendarEvents.value && isDataLoaded.value
})

const calendarOptions = computed(() => {
  return {
    plugins: [resourceDayGridPlugin, interactionPlugin, resourceTimeGridPlugin, timeGridPlugin],
    schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
    locale: 'fr-FR',
    weekText: 'S',
    initialView: isManager.value && !props.partnerOnly ? 'resourceTimeGridWeek' : 'timeGridWeek',
    height: 'auto',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: isManager.value && !props.partnerOnly
        ? 'resourceDayGridMonth,resourceTimeGridWeek,resourceTimeGridDay'
        : 'dayGridMonth,timeGridWeek,timeGridDay',
    },
    allDaySlot: false,
    weekNumbers: true,
    businessHours: {
      startTime: '07:00',
      endTime: '19:00',
    },
    editable: true,
    slotMinTime: '06:00:00',
    slotMaxTime: '20:00:00',
    nowIndicator: true,
    eventClick: handleDateClick,
    weekends: false,
    resources: resourcesFullcalendar.value,
    events: calendarEvents.value,
    eventDidMount: (info: any) => {
      const eventData = info.event.extendedProps
      const isCreatedByUser = eventStore.isEventCreatedByUser(eventData)
      if (!isCreatedByUser) {
        const eventEl = info.el
        eventEl.style.position = 'relative'
        // Visually indicate non-editable: default cursor
        eventEl.style.cursor = 'default'

        const iconContainer = document.createElement('div')
        iconContainer.classList.add('absolute')
        iconContainer.classList.add('top-0')
        iconContainer.classList.add('right-0')
        iconContainer.classList.add('p-1')
        iconContainer.textContent = '✉️'
        eventEl.appendChild(iconContainer)
      }

      const tooltipEl = document.createElement('div')
      tooltipEl.classList.add('bg-base-100')
      tooltipEl.classList.add('p-2')
      tooltipEl.classList.add('rounded-md')
      tooltipEl.classList.add('w-96')
      tooltipEl.classList.add('text-sm')

      let tooltipHTML = `<div class="card-title">${eventData.name}</div>
        <div class="mt-3">
          <b>Description :</b>
          ${eventData.description || ''}
        </div>
        <div class="mt-3">
          <b>Organisateur :</b>
          ${eventData.userId ? userStore.getLocalInfoUserById(eventData.userId)?.name : ''}
        </div>
        <div class="mt-3">
          <b>Société :</b>
          ${eventData.partnerSelected && eventData.partnerSelected.length > 0 ? eventData.partnerSelected[1] : ''}
        </div>`
      if(eventData.partnerIds.length > 0) {
        tooltipHTML += `
          <div class="mt-3">
            <b>Invités :</b>
            <div class="overflow-x-auto">
              <table class="table table-xs">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  ${eventData.partnerIds
                    .map(
                      (partner: Partner) => `
                    <tr>
                      <td>${partner.name || 'N/A'}</td>
                      <td>${partner.email || 'N/A'}</td>
                    </tr>
                  `,
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          </div>
        `
      }

      tooltipEl.innerHTML = tooltipHTML

      tooltipEl.style.display = 'none'
      tooltipEl.style.position = 'absolute'
      tooltipEl.style.zIndex = '9999'

      document.body.appendChild(tooltipEl)
      // Tooltip helpers (suppressed if mouse is currently pressed anywhere)
      const showTooltip = () => {
        if (isMouseDownGlobal.value) return
        const rect = info.el.getBoundingClientRect()
        tooltipEl.style.display = 'block'
        tooltipEl.style.left = `${rect.left + window.scrollX}px`
        tooltipEl.style.top = `${rect.top + window.scrollY - tooltipEl.offsetHeight - 10}px`
      }

      const hideTooltip = () => {
        tooltipEl.style.display = 'none'
      }

      const onMouseEnter = () => showTooltip()
      const onMouseLeave = () => hideTooltip()
      const onMouseDown = () => {
        isMouseDownGlobal.value = true
        hideTooltip()
      }
      const onMouseUp = () => {
        isMouseDownGlobal.value = false
        hideTooltip()
      }

      info.el.addEventListener('mouseenter', onMouseEnter)
      info.el.addEventListener('mouseleave', onMouseLeave)
      info.el.addEventListener('mousedown', onMouseDown)
      info.el.addEventListener('mouseup', onMouseUp)

      info.el.addEventListener('eventDestroy', () => {
        hideTooltip()
        tooltipEl.remove()
        info.el.removeEventListener('mouseenter', onMouseEnter)
        info.el.removeEventListener('mouseleave', onMouseLeave)
        info.el.removeEventListener('mousedown', onMouseDown)
        info.el.removeEventListener('mouseup', onMouseUp)
        document.removeEventListener('mousedown', onMouseDown)
        document.removeEventListener('mouseup', onMouseUp)
      })
    },
    viewDidMount: function () {
      setTimeout(() => {
        addColors()
      }, 0)
    },
    datesSet: function (info: { startStr: string; endStr: string }) {
      if (!props.partnerOnly) {
        const startDate = info.startStr.split('T')[0]
        const endDate = info.endStr.split('T')[0]
        eventStore.getEventsSubordinatesId(startDate, endDate)
      }
    },
    dateClick: function (info: any) {
      const event = new CustomEvent('open-event-create-modal', { detail: { date: info.date } })
      window.dispatchEvent(event)
    },
    eventDragStart: function (info: any) {
      // Hide/suppress tooltips during drag operations
      // Force-hide any tooltip tied to this element
      try {
        info.el.dispatchEvent(new Event('mouseleave'))
      } catch (e) {}
    },
    eventDragStop: function (info: any) {
      // Ensure tooltip stays hidden after drop
      try {
        info.el.dispatchEvent(new Event('mouseleave'))
      } catch (e) {}
    },
    eventAllow: function (dropInfo: any, draggedEvent: any) {
      // Disallow moving/resizing events not created by current user
      const eventData = draggedEvent.extendedProps || {}
      return eventStore.isEventCreatedByUser(eventData)
    },
    eventDrop: async function (info: any) {
      // Persist time/date changes when dragging events
      const e = info.event
      const eventData = e.extendedProps || {}
      if (!eventStore.isEventCreatedByUser(eventData)) {
        try {
          info.revert()
        } catch (_) {}
        return
      }
      const start = e.start ? new Date(e.start) : null
      const end = e.end ? new Date(e.end) : null
      if (!start || !end) {
        try {
          info.revert()
        } catch (_) {}
        return
      }
      const duration = calculateDurationWithFormat(start, end)
      const updateEventDto = {
        name: eventData.name ?? e.title ?? null,
        start: removeTimeZone(start),
        duration: duration,
        partnerIds: {},
        categIds: eventData.categIds ?? 0,
        description: eventData.description ?? '',
        userId: eventData.userId ?? null,
        location: eventData.location ?? undefined,
        partnerSelected: (eventData.partnerSelected && eventData.partnerSelected.id) || null,
      }
      const partnerIdsExisting = (eventData.partnerIds || []).map((p: any) => p.id)
      const partnerIdsAll = (eventData.partnerIds || []).map((p: any) => p.id)
      try {
        await eventStore.updateEvent(
          { id: parseInt(e.id), partnerIds: partnerIdsExisting },
          updateEventDto,
          partnerIdsAll,
          '',
        )
        $toast.success('Rendez-vous mis à jour avec succès')
      } catch (err) {
        try {
          info.revert()
        } catch (_) {}
      }
    },
    eventResize: async function (info: any) {
      // Persist duration changes when resizing events
      const e = info.event
      const eventData = e.extendedProps || {}
      if (!eventStore.isEventCreatedByUser(eventData)) {
        try {
          info.revert()
        } catch (_) {}
        return
      }
      const start = e.start ? new Date(e.start) : null
      const end = e.end ? new Date(e.end) : null
      if (!start || !end) {
        try {
          info.revert()
        } catch (_) {}
        return
      }
      const duration = calculateDurationWithFormat(start, end)
      const updateEventDto = {
        name: eventData.name ?? e.title ?? null,
        start: removeTimeZone(start),
        duration: duration,
        partnerIds: {},
        categIds: eventData.categIds ?? 0,
        description: eventData.description ?? '',
        userId: eventData.userId ?? null,
        location: eventData.location ?? undefined,
        partnerSelected: (eventData.partnerSelected && eventData.partnerSelected.id) || null,
      }
      const partnerIdsExisting = (eventData.partnerIds || []).map((p: any) => p.id)
      const partnerIdsAll = (eventData.partnerIds || []).map((p: any) => p.id)
      try {
        await eventStore.updateEvent(
          { id: parseInt(e.id), partnerIds: partnerIdsExisting },
          updateEventDto,
          partnerIdsAll,
          '',
        )
        $toast.success('Rendez-vous mis à jour avec succès')
      } catch (err) {
        try {
          info.revert()
        } catch (_) {}
      }
    },
    buttonText: {
      today: "Aujourd'hui",
      month: 'Mois',
      week: 'Semaine',
      day: 'Jour',
    },
  }
})

function handleDateClick(arg: any) {
  selectedEventId.value = parseInt(arg.event.id)
  showEventModal.value = true
}

const handleClose = () => {
  showEventModal.value = false
}

const handleChange = () => {
  const calendarApi = calendarRef.value?.getApi()
  if (selectedDate.value) calendarApi?.gotoDate(selectedDate.value.toISOString())
}

function addColors() {
  const headers: NodeListOf<Element> = document.querySelectorAll('.fc-col-header-cell.fc-resource')
  for (let i = 0; i < headers.length; i++) {
    headers[i].setAttribute('style', `background-color: ${colors[i]}; color: white;`)
  }
}

watch(
  filters,
  () => {
    setTimeout(() => {
      addColors()
    }, 0)
  },
  { immediate: true, deep: true },
)

watch(
  drawerOpen,
  (newDrawerOpen, oldDrawerOpen) => {
    if (newDrawerOpen !== oldDrawerOpen) {
      setTimeout(() => {
        calendarRef.value?.getApi().updateSize()
      }, 300)
    }
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <div class="grid gap-4">
    <div class="flex gap-3 items-center">
      <div class="w-fit items-center">
        <Datepicker
          v-model="selectedDate"
          locale="fr"
          week-numbers="local"
          week-num-name="S"
          auto-apply
          :enable-time-picker="false"
          format="dd/MM/yyyy"
          :disabled-week-days="[6, 0]"
          @update:model-value="handleChange"
          class="w-min"
        />
      </div>
      <EventCreate v-if="showAddButton" @close="handleClose" />
      <CommercialFilter
        v-if="isManager && userConnected && showFilterCommercial"
        v-model="filters.selectedCommercials"
      />
      <div v-if="loadingDiary" class="loading loading-spinner loading-lg"></div>
    </div>

    <!-- Indicateur de chargement (optionnel) -->
    <div v-if="!shouldShowCalendar" class="flex justify-center items-center h-64">
      <span class="ml-2">Chargement du calendrier...</span>
    </div>

    <!-- FullCalendar avec condition de chargement -->
    <FullCalendar
      ref="calendarRef"
      id="calendar"
      v-if="shouldShowCalendar"
      :options="calendarOptions"
    >
    </FullCalendar>
  </div>
  <EventEdit
    v-if="showEventModal && selectedEventId"
    :eventId="selectedEventId"
    @close="handleClose"
  />
</template>
